class EsimatedArrival {
  constructor(holidays, dateSpans, dateExpressSpans, dateOvernightSpans) {
    this.holidays = holidays;
    this.dateSpans = dateSpans;
    this.dateExpressSpans = dateExpressSpans;
    this.dateOvernightSpans = dateOvernightSpans;
    this.debug = false;
    this.debugContainer = document.querySelector(".estimated-arrival-debug");
    this.debugInput = document.querySelector("#shipping-debug-date");
    this.debugOutput = document.querySelector(".shipping-debug__output pre");

    this.storeData = {
      timezone: "America/Chicago",
      cutoffHour: 2,
      cutoffMinute: 0,
      maxDayExpected: 7,
      minDayExpected: 5,
      expressDayExpected: 2,
      overnightDayExpected: 1,
      holidays: this.holidays,
    };

    if (this.debugContainer && this.debugInput) {
      this.debugInput.addEventListener("input", () => {
        this.debugOutput.innerHTML = "";
        this.ready();
      });
    }

    this.ready();
  }

  ready() {
    this.now = this.convertTimeZone(new Date(), this.storeData.timezone);

    if (this.debugContainer && this.debugInput) {
      this.now = this.convertTimeZone(
        new Date(this.debugInput.value),
        this.storeData.timezone
      );
    }

    if (this.debug) this.debugHelper("now: " + this.now);
    this.cutoff = new Date(this.now);
    this.cutoff.setHours(
      this.storeData.cutoffHour,
      this.storeData.cutoffMinute,
      0,
      0
    );
    if (this.debug) this.debugHelper("cutoffDate: " + this.cutoff);
    this.shipDate = this.getShipDate();
    if (this.debug) this.debugHelper("shipDate: " + this.shipDate);

    let maxStandard = this.getArrivalDate(this.storeData.maxDayExpected, true);
    let minStandard = this.getArrivalDate(this.storeData.minDayExpected);
    let express = this.getArrivalDate(this.storeData.expressDayExpected);
    let overnight = this.getArrivalDate(this.storeData.overnightDayExpected);

    this._minStandard = minStandard;
    this._maxStandard = maxStandard;
    this._express = express;
    this._overnight = overnight;

    this.paintAll();
    this.observeNewSpans();
  }

  paintAll() {
    this.dateSpans.forEach((span) =>
      this.paintSpan(span, this._minStandard, this._maxStandard)
    );
    this.dateExpressSpans.forEach((span) =>
      this.paintSpan(span, this._express)
    );
    this.dateOvernightSpans.forEach((span) =>
      this.paintSpan(span, this._overnight)
    );
  }

  paintSpan(span, ...dates) {
    let useCompact = span.dataset.compact === "true";
    let fmt = dates.map((d) =>
      useCompact ? d.short.formatedDate : d.long.formatedDate
    );
    let text = fmt.length > 1 ? fmt.join(" - ") : fmt[0];
    this.setElementContent(span, text);
  }

  /**
   * Gets the arrival date from the store data
   * @param {Integer} daysExpected the number of days expected to arrive
   * @returns {Object} the arrival dates
   */
  getArrivalDate(daysExpected, debug = false) {
    let d = new Date(this.shipDate);

    const holidayMDs = this.storeData.holidays.map((h) =>
      h.split(",")[0].trim().toLowerCase()
    );

    if (debug) {
      if (this.debug) this.debugHelper("holidayMDs", holidayMDs);
    }

    let counted = 0;
    let skipped = 0;
    while (counted < daysExpected) {
      d.setDate(d.getDate() + 1);

      const dow = d.getDay(); // 0=Sun, 6=Sat
      const md = d
        .toLocaleString("en-US", { month: "long", day: "2-digit" })
        .toLowerCase();

      // if NOT weekend (isweekend) and NOT holiday, count it
      if (!this.isWeekend(d) && !holidayMDs.includes(md)) {
        if (debug && this.debug) {
          this.debugHelper("counted", getDayName(d), d);
        }
        counted++;
      } else {
        skipped++;
        if (debug && this.debug) {
          this.debugHelper("skipped", getDayName(d), d);
        }
      }
    }

    if (debug) {
      if (this.debug) this.debugHelper("counted", counted, "skipped", skipped);
    }

    const warehouseDate = new Date(d);
    let day = warehouseDate.toLocaleString("en-US", { day: "2-digit" });
    let monthLong = warehouseDate.toLocaleString("en-US", { month: "long" });
    let monthShort = warehouseDate.toLocaleString("en-US", { month: "short" });
    let weekDayLong = warehouseDate.toLocaleString("en-US", {
      weekday: "long",
    });
    let weekDayShort = warehouseDate.toLocaleString("en-US", {
      weekday: "short",
    });
    let year = warehouseDate.toLocaleString("en-US", { year: "numeric" });

    let response = {
      short: {
        weekDay: weekDayShort,
        day: day,
        month: monthShort,
        year,
        formatedDate: `${weekDayShort}, ${monthShort} ${day}`,
      },
      long: {
        weekDay: weekDayLong,
        day: day,
        month: monthLong,
        year,
        formatedDate: `${weekDayLong}, ${monthLong} ${day}`,
      },
    };
    // if (this.debug) this.debugHelper("getArrivalDate", response);

    return response;
  }

  getShipDate() {
    const shipDate = new Date(this.now);
    if (this.now > this.cutoff) {
      shipDate.setDate(shipDate.getDate() + 1);
      if (this.debug)
        this.debugHelper("now > cutoff", "adding one day", shipDate);
    }

    while (this.isWeekend(shipDate) || this.isHoliday(shipDate)) {
      shipDate.setDate(shipDate.getDate() + 1);
    }

    return shipDate;
  }

  isWeekend(d) {
    return d.getDay() === 6 || d.getDay() === 0;
  }

  isHoliday(d) {
    const md = d
      .toLocaleString("en-US", { month: "long", day: "2-digit" })
      .toLowerCase();
    return this.storeData.holidays
      .map((h) => h.split(",")[0].trim().toLowerCase())
      .includes(md);
  }

  /**
   * @param {NodeElement} element to set the content
   * @param {String} content to be displayed at the element
   */
  setElementContent(element, content) {
    if (!element) return false;
    element.innerHTML = content;
  }

  /**
   * Fill the shipping estimated arrival graphic
   * @param {String} arrival date as string to be displayed as arrival date
   */
  setGraphicDates(arrival) {
    const orderDate = this.querySelector("[data-order-date]");
    const shipsDate = this.querySelector("[data-ships-date]");
    const arrivalDate = this.querySelector("[data-arrival-date]");
    const today = new Date(
      this.convertTimeZone(new Date(), this.storeData.timezone)
    );
    let month = today.toLocaleString("en-US", { month: "long" });
    let day = today.toLocaleString("en-US", { day: "2-digit" });

    const shipEstimate = this.getShipDate();

    this.setElementContent(orderDate, `${month} ${day}`);
    this.setElementContent(
      shipsDate,
      `${shipEstimate.month} ${shipEstimate.day}`
    );
    this.setElementContent(arrivalDate, arrival);
  }

  /**
   * Converts a date to a given timezone
   * @param {Date} date the date to be converted
   * @param {String} timezone the timezone to convert the date to
   * @returns {Date} the converted date
   */
  convertTimeZone(date, timezone) {
    const dateString = date.toLocaleString("en-US", {
      timeZone: timezone,
    });
    return new Date(dateString);
  }

  debugHelper(...args) {
    if (!this.debugContainer || !this.debugOutput) {
      console.log(...args);
      return;
    }

    const line = args
      .map((arg) =>
        typeof arg === "object" ? JSON.stringify(arg, null, 2) : String(arg)
      )
      .join(" ");

    this.debugOutput.innerHTML += line + "<br>";
  }

  observeNewSpans() {
    const observer = new MutationObserver((mutations) => {
      for (let m of mutations) {
        for (let node of m.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;

          // if the added node *is* one of our spans
          if (
            node.matches(
              "span[data-date], span[data-express-date], span[data-overnight-date]"
            )
          ) {
            this.handleNewSpan(node);
          }

          // or if it *contains* any of those spans
          node.querySelectorAll &&
            node
              .querySelectorAll(
                "span[data-date], span[data-express-date], span[data-overnight-date]"
              )
              .forEach((el) => {
                this.handleNewSpan(el);
              });
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  handleNewSpan(span) {
    // add it to the right array so future calls to paintAll() include it
    if (span.matches("span[data-date]")) {
      this.dateSpans.push(span);
      this.paintSpan(span, this._minStandard, this._maxStandard);
    }
    if (span.matches("span[data-express-date]")) {
      this.dateExpressSpans.push(span);
      this.paintSpan(span, this._express);
    }
    if (span.matches("span[data-overnight-date]")) {
      this.dateOvernightSpans.push(span);
      this.paintSpan(span, this._overnight);
    }
  }
}

function getDayName(dateStr, locale) {
  var date = new Date(dateStr);
  return date.toLocaleDateString(locale, { weekday: "long" });
}

const holidays =
  document.querySelector("#estimated-arrival-holidays").textContent ?? null;

if (holidays) {
  const holidaysArray = holidays.split(";");
  const dateSpans = Array.from(document.querySelectorAll("span[data-date]"));
  const dateExpressSpans = Array.from(
    document.querySelectorAll("span[data-express-date]")
  );
  const dateOvernightSpans = Array.from(
    document.querySelectorAll("span[data-overnight-date]")
  );

  new EsimatedArrival(
    holidaysArray,
    dateSpans,
    dateExpressSpans,
    dateOvernightSpans
  );
}
